import { ActivatedRouteSnapshot } from '@angular/router';

/** Report types the portal can send to the proAV.Net team (`POST /api/portal-feedback`). */
export type PortalFeedbackKind = 'bug' | 'feature';

export type BugImpact = 'blocking' | 'major' | 'minor' | 'cosmetic';
export type FeatureImportance = 'nice-to-have' | 'important' | 'critical';

export const BUG_IMPACTS: BugImpact[] = ['blocking', 'major', 'minor', 'cosmetic'];
export const FEATURE_IMPORTANCES: FeatureImportance[] = ['nice-to-have', 'important', 'critical'];

/** Where the user was when they opened the dialog. The server redacts credentials from `url`. */
export interface PortalFeedbackPageContext {
    url: string;
    /** Matched route pattern, e.g. `/device/:deviceId/logs`. */
    route: string | null;
    /** The page's main heading, falling back to the document title. */
    pageTitle: string | null;
    namespaceId: string | null;
    uiVersion: string | null;
    viewport: string | null;
    language: string | null;
    timeZone: string | null;
}

export interface PortalFeedbackRequest {
    kind: PortalFeedbackKind;
    title: string;
    summary: string;
    stepsToReproduce?: string | null;
    expectedBehaviour?: string | null;
    actualBehaviour?: string | null;
    impact?: BugImpact | null;
    problem?: string | null;
    proposedSolution?: string | null;
    importance?: FeatureImportance | null;
    context: PortalFeedbackPageContext;
    /** An uploaded screenshot (`POST /api/feedback-screenshots`) to embed in the issue. */
    screenshotId?: string | null;
}

/** `htmlUrl` is only returned to super admins, who can open the platform repository. */
export interface PortalFeedbackResponse {
    kind: PortalFeedbackKind;
    number: number;
    htmlUrl: string | null;
}

/** `POST /api/feedback-screenshots`. `url` is where the issue will load it from. */
export interface FeedbackScreenshotUpload {
    id: string;
    url: string | null;
    contentType: string;
    sizeBytes: number;
}

/** One of the user's open reports (`GET /api/portal-feedback/mine`). `htmlUrl` is only returned to super admins. */
export interface PortalFeedbackReport {
    number: number;
    /** Without the [Bug] / [Feature] prefix. */
    title: string;
    kind: PortalFeedbackKind;
    createdUtc: string;
    updatedUtc: string;
    htmlUrl: string | null;
}

/** `POST /api/portal-feedback/{number}/updates`: more information, and optionally another screenshot, for an earlier report. */
export interface PortalFeedbackUpdateRequest {
    text: string;
    context: PortalFeedbackPageContext;
    screenshotId?: string | null;
}

/** `commentUrl` is only returned to super admins. */
export interface PortalFeedbackUpdateResponse {
    number: number;
    commentUrl: string | null;
}

export interface PortalFeedbackStatus {
    enabled: boolean;
    /** Only returned to super admins. */
    repository: string | null;
}

export const PORTAL_FEEDBACK_MAX_TITLE = 120;
export const PORTAL_FEEDBACK_MAX_FIELD = 8000;

/** The route pattern of the deepest active route, e.g. `/dashboard/:namespaceId/:locationId`. */
export function routePattern(root: ActivatedRouteSnapshot): string | null {
    const segments: string[] = [];
    let node: ActivatedRouteSnapshot | null = root;
    while (node) {
        const path = node.routeConfig?.path;
        if (path) segments.push(path);
        node = node.firstChild;
    }
    return segments.length ? '/' + segments.join('/') : null;
}

/** Collects the page context from the browser. `doc`/`win` are injectable for tests. */
export function capturePageContext(options: { root: ActivatedRouteSnapshot; namespaceId: string | null; uiVersion: string | null; doc?: Document; win?: Window }): PortalFeedbackPageContext {
    const doc = options.doc ?? document;
    const win = options.win ?? window;
    const heading = doc.querySelector('.layout-main h1, .layout-main h2, .layout-main h3')?.textContent?.replace(/\s+/g, ' ').trim();
    let timeZone: string | null = null;
    try {
        timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    } catch {
        timeZone = null;
    }
    return {
        url: win.location.href,
        route: routePattern(options.root),
        pageTitle: (heading || doc.title || '').slice(0, 200) || null,
        namespaceId: options.namespaceId,
        uiVersion: options.uiVersion,
        viewport: `${win.innerWidth}x${win.innerHeight}`,
        language: win.navigator?.language ?? null,
        timeZone
    };
}

/** Drops whitespace-only optional fields and the fields of the other report type. */
export function buildPortalFeedbackRequest(kind: PortalFeedbackKind, fields: Partial<Record<string, string>>, choice: string | null, context: PortalFeedbackPageContext): PortalFeedbackRequest {
    const value = (key: string) => (fields[key] ?? '').trim() || null;
    const request: PortalFeedbackRequest = { kind, title: (fields['title'] ?? '').trim(), summary: (fields['summary'] ?? '').trim(), context };
    if (kind === 'bug') {
        request.stepsToReproduce = value('stepsToReproduce');
        request.expectedBehaviour = value('expectedBehaviour');
        request.actualBehaviour = value('actualBehaviour');
        request.impact = (choice as BugImpact | null) ?? null;
    } else {
        request.problem = value('problem');
        request.proposedSolution = value('proposedSolution');
        request.importance = (choice as FeatureImportance | null) ?? null;
    }
    return request;
}
